import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const photos = [
  { src: "/assets/job7.webp", alt: "Technician with core drill rig on an asphalt roadway", label: "Asphalt Coring" },
  { src: "/assets/job10.webp", alt: "Drill rig and crew completing a borehole with monitoring pipe installed", label: "Geotechnical Drilling" },
  { src: "/assets/n1.webp", alt: "Two crew members running core drills on asphalt at night", label: "Night Coring Operations" },
  { src: "/assets/job8.webp", alt: "Work truck and coring rig set up with traffic cones on a parking lot", label: "Parking Lot Coring" },
  { src: "/assets/job11.webp", alt: "Crew operating a tall drill mast for soil borings on a grassy site", label: "Soil Borings" },
  { src: "/assets/n2.webp", alt: "Night coring crew working downtown beside work trucks with light tower", label: "Downtown Night Work" },
  { src: "/assets/job1.webp", alt: "Crew member coring pavement beside work truck", label: "Pavement Cores" },
  { src: "/assets/n3.webp", alt: "Three crew members setting up a nighttime coring work zone", label: "Night Crew Setup" },
  { src: "/assets/job5.webp", alt: "Fresh asphalt placement on a Houston-area roadway", label: "Roadway Projects" },
  { src: "/assets/n4.webp", alt: "Crew working under lights with traffic control at night", label: "Traffic Control & Safety" },
  { src: "/assets/job6.webp", alt: "Bridge deck project over water", label: "Bridge & Infrastructure" },
  { src: "/assets/n5.webp", alt: "Close-up of core drills extracting pavement cores at night", label: "Core Extraction" },
];

export default function Gallery() {
  const [selected, setSelected] = useState(null);

  const close = useCallback(() => setSelected(null), []);
  const step = useCallback(
    (dir) => setSelected((s) => (s === null ? null : (s + dir + photos.length) % photos.length)),
    []
  );

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, close, step]);

  return (
    <section id="work" className="py-24 lg:py-32 bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-4 font-mono-tech" data-testid="gallery-overline">
            Recent Work • 01
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="gallery-heading">
            In the Field
          </h2>
          <p className="mt-6 text-base text-slate-600 leading-relaxed">
            Real crews, real equipment, real results on job sites across Greater Houston. Tap any photo to view it full-screen.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6" data-testid="gallery-grid">
          {photos.map((p, i) => (
            <motion.figure
              key={p.src}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.08, ease: "easeOut" }}
              className="group relative overflow-hidden clip-frame border border-slate-200 transition-all duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0F172A] lg:col-span-2 cursor-zoom-in"
              data-testid={`gallery-item-${i}`}
              onClick={() => setSelected(i)}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="w-full h-72 object-cover grayscale-[35%] group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-[#0F172A]/85 px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wider text-white">{p.label}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-[#0F172A]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={close}
            data-testid="lightbox-overlay"
          >
            <button
              onClick={close}
              className="absolute top-5 right-5 text-white/80 hover:text-[#FF5A00] transition-colors p-2"
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 sm:left-8 text-white/80 hover:text-[#FF5A00] transition-colors p-2"
              aria-label="Previous photo"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
            <motion.figure
              key={selected}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photos[selected].src}
                alt={photos[selected].alt}
                className="w-full max-h-[78vh] object-contain rounded-sm"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-4 text-center" data-testid="lightbox-caption">
                <span className="font-heading text-sm font-bold uppercase tracking-wider text-white">
                  {photos[selected].label}
                </span>
                <span className="text-slate-400 text-sm ml-3 font-mono-tech">
                  {selected + 1} / {photos.length}
                </span>
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 sm:right-8 text-white/80 hover:text-[#FF5A00] transition-colors p-2"
              aria-label="Next photo"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
